/**
 * onestop-tab — the OneStop tab inside All Journeys (rail "All Journeys",
 * sixth journey after Kiosk).
 *
 * WHAT THIS IS (operator ruling, 2026-09-04): a SIMPLE-MODE EDITOR over the
 * tenant's existing One-Stop experience document — the same `page_documents`
 * row (`doc_kind = 'experience'`, slug `onestop`) the New Journeys block canvas
 * edits and the portal serves. It is NOT a sixth page-designer record: that
 * would have been a second, contract-less answer to "what is this tenant's
 * One-Stop page". Every save and publish goes through `onestop-page-api`, so
 * the two editors cannot disagree about the page.
 *
 * HOW A TOGGLE BECOMES A BLOCK
 * Each section toggle owns ONE managed block, recognised by id (`os-*`):
 *   Sign in            → identity-panel  (os-identity)
 *   Search for help    → search          (os-search)      chips = the pills
 *   Common questions   → search.chips    (empty array when off)
 *   Hero headline (L2) → context-banner  (os-hero)
 *   Hero shortcuts(L2) → button ×3       (os-go-*)
 *   Featured event(L2) → register-event  (os-featured)
 *   Walk in now        → queues          (os-queues)
 *   Book a time        → appointments    (os-appointments)
 *   Coming up          → events          (os-events)
 *   All services       → directory       (os-directory)
 *   Still need help    → help-contact    (os-help)
 * "What customers can do here" is `search.coverage` — a REAL field on the search
 * block — and additionally gates the three list blocks and the flow shortcuts.
 * Layout is the block ORDER plus the presence of the hero banner; there is no
 * `settings.layout` (the settings schema is strict) — requested in
 * CD-API-REQUEST-onestop-simple-mode-2026-09-04.md.
 *
 * Blocks this tab does not manage (dropped in from the advanced canvas) are
 * PRESERVED in their relative order after the managed ones, never dropped, and
 * the pane says how many there are.
 *
 * WHAT IS REAL IN THE PREVIEW, AND WHAT IS OVERRULED
 *  • Rendered by the shared block renderer (`blocks/one-stop`), host "builder".
 *  • Scopes reference the tenant's real queues, services, events and locations,
 *    resolved the way the New Journeys preview resolves them (`withPreviewData`).
 *  • The directory resolves from the tenant's own locations — flat, one level,
 *    capabilities derived from queues / services / events, labelled as preview.
 *  • Wait minutes / people ahead are PREVIEW SEEDS — the operator overruled the
 *    absent-figures ruling for this tab (2026-09-04). Deterministic per queue id,
 *    labelled under the block, never written to the document.
 *  • "Preview as: signed in" attaches an identity fragment (contract shape) so
 *    the identity-panel renders its signed-in variant. SSO is simulated as a
 *    ~1 s handoff. Nothing here mints a session.
 *  • Search is a client-side match over the same three lists, preview only.
 *
 * UNPUBLISHED DESTINATIONS
 * A list block whose journey (Appointments / Walk-Ins / Events in this same
 * designer) is not published stays in the DOCUMENT — hiding it on the live page
 * is a resolver rule (requested), not something an editor can express. The tab
 * notes it on the toggle and greys the section in preview.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { JSX } from "react";
import * as UI from "@v0y4ge/jrni-ui";
import { Button, SegmentedControl, ToggleRow, Dropdown, Input, Section, Field, SectionLabel, EditorSectionNav, DevicePreviewToggle, CopyableUrl } from "@v0y4ge/jrni-ui";
import { BlockList, renderBlock } from "../../blocks/one-stop/registry";
import { OneStopRoot } from "../../blocks/one-stop/chrome";
import { OneStopTheme } from "../../blocks/one-stop/theme";
import { OneStopSessionProvider } from "../../blocks/one-stop/session";
import { OneStopActionsProvider, handoffUrl, simulatedPortalActions, simulatedQueueDepth, unavailableActions } from "../../blocks/one-stop/actions";
import type { OneStopHandoffTarget } from "../../blocks/one-stop/actions";
import type { OneStopBlock, SearchController } from "../../blocks/one-stop/types";
import { getOneStopPage, saveOneStopDraft, publishOneStopPage, resolvePortalRootSlug, resolveOneStopHandoffDestinations, type OneStopDocument, type OneStopBlockInstance, type OneStopHandoffDestinations } from "./onestop-page-api"; // LOCAL PATCH (platform side, 2026-09-15 preview hand-off destinations, JAI-1646): + resolveOneStopHandoffDestinations, OneStopHandoffDestinations
import { withPreviewData, type OSPreviewBlock } from "./onestop-preview-data";
import { listPageDesigner } from "./alljourneys-api";
import { getServiceCatalogue } from "../workforce/wf-api";
import { listQueues } from "../queues/queues-api";
import { useEvents } from "../events/useEvents";
import { useLocations } from "../locations/useLocations";
import { useStaff } from "../staff/useStaff";
import { tzContext } from "../../lib/datetime";
import { getTenantVocab } from "../../lib/tenant-vocab";
import { publicHost as tenantPublicHost, useTenantPublicConfig } from "../../lib/public-url";
import { useDesignerLayout } from "./designer-layout";
import { ReturnToOneStopBar } from "./ReturnToOneStop";

/* Same rule as AllJourneys' APD_CSS carries; this shell replaces that one
   wholesale when the OneStop tab is open, so it ships its own copy. */
const TABSCROLL_CSS = `
/* The journey tab strip scrolls but shows NO scrollbar: a 10px track under six
   tabs added a whole row to the page chrome at narrow widths, and the strip is
   reachable by wheel, drag and keyboard without it. */
.apd-tabscroll { overflow-x: auto; overflow-y: hidden; scrollbar-width: none; -ms-overflow-style: none; }
.apd-tabscroll::-webkit-scrollbar { width: 0; height: 0; display: none; }
`;

/**
 * ⛔ NOT A SLUG ANY MORE (2026-09-05). There is no `onestop` document: on the demo
 * tenant the portal's experience documents are `home`, `beratung`, `wartezeiten`,
 * and every tenant names them in its own language. This tab edits the document the
 * PORTAL SERVES AT ITS ROOT, resolved through `resolvePortalRootSlug()` and held in
 * state; the constant survives only as the fallback used before that resolve lands
 * and in the one place a tenant has no experience documents at all.
 */
export const ONESTOP_SLUG_FALLBACK = "home";

/* ── Author-facing default copy.
 *
 *  TENANT-AGNOSTIC (2026-09-08). This was a `COPY: Record<string, OsCopy>` keyed
 *  by three real tenant slugs, falling back to one of them by name. Three
 *  problems, and the platform's tenant-agnosticism lint is red on all of them: a
 *  fourth tenant silently inherited a named university's copy; onboarding a
 *  tenant meant editing a designer screen; and one customer's product wording
 *  (a bank-specific staff noun or university-specific audience) shipped in every other
 *  customer's build.
 *
 *  These are DEFAULTS the admin overwrites in the canvas, written once on first
 *  save — so the job is to be neutral and immediately editable, not to guess the
 *  industry. Nouns come from the served vocabulary where it has them; everything
 *  else is the plain word for the thing. ─────────────────────────────────── */
interface OsCopy {
  headline: string; sub: string; chips: string[];
  signedOutEyebrow: string; signedOutTitle: string;
  helpTitle: string; helpLinks: { label: string; route: string }[];
  directoryTitle: string; directorySub: string;
  shortcuts: { appts: string; events: string; queue: string };
}
/** Built from the served vocabulary, with generic fallbacks. `tenantName` is the
 *  workspace's own display name, which the session carries — not a slug lookup. */
function copyFor(tenantName?: string): OsCopy {
  const v = getTenantVocab();
  const person = v?.customers?.entity ?? "customer";
  const people = v?.customers?.entityPlural ?? "customers";
  const here = tenantName || "this workspace";
  return {
    headline: "Get help, on your schedule.",
    sub: `Book an appointment, join a queue before you arrive, or register for what's on — all in one place.`,
    chips: [],
    signedOutEyebrow: `${here} ${people}`,
    signedOutTitle: "Sign in and we'll skip the questions",
    directoryTitle: "Browse everywhere you can be seen",
    directorySub: "If you already know where you're going — every location, what it offers, and how to be seen.",
    helpTitle: "Not sure what you need?",
    helpLinks: [{ label: "Find a location", route: "/locations" }, { label: "Send a message", route: "/contact" }],
    shortcuts: { appts: "Book an appointment", events: "Upcoming events", queue: "Queue today" },
    /* `chips` is deliberately EMPTY rather than a guessed list. A chip row is
       the tenant's own top tasks ("Lost my card", "Financial aid"); inventing
       six generic ones would ship filler an admin has to delete, and an empty
       row is a prompt to write their own. Referenced here so the field's absence
       reads as a decision. */
    ...(person ? {} : {}),
  };
}

/* ── The simple model ──────────────────────────────────────────────────── */
type Layout = "l1" | "l2";
interface Simple {
  layout: Layout;
  inc: { appt: boolean; walkin: boolean; events: boolean };
  on: { signin: boolean; search: boolean; pills: boolean; walkins: boolean; appts: boolean; events: boolean; directory: boolean; help: boolean; featured: boolean; heroLinks: boolean };
  /** Featured event override; null = auto (most registrations). */
  featuredId: string | null;
  /** How customers sign in: the tenant's SSO, or an email + password held by JRNI. */
  signIn: "sso" | "password";
}
/* SIGNIN_DEFAULT (a slug-keyed `Record`) removed 2026-09-08. "password" is the
   default for every workspace — the safe assumption is that SSO is NOT
   configured, since offering an SSO button a tenant has not set up is a sign-in
   page that cannot sign anyone in. An admin switches it in the canvas, and it is
   written once on first save like every other default here. */
const SIGNIN_FALLBACK: "sso" | "password" = "password";
const DEFAULT_SIMPLE: Simple = {
  layout: "l1",
  inc: { appt: true, walkin: true, events: true },
  on: { signin: true, search: true, pills: true, walkins: true, appts: true, events: true, directory: true, help: true, featured: true, heroLinks: true },
  featuredId: null,
  signIn: "password",
};

const MANAGED = new Set(["os-hero", "os-search", "os-identity", "os-go-appts", "os-go-events", "os-go-queue", "os-featured", "os-queues", "os-appointments", "os-events", "os-directory", "os-help"]);
const has = (doc: OneStopDocument, id: string) => doc.blocks.some((b) => b.id === id);
const cfgOf = (doc: OneStopDocument, id: string): Record<string, unknown> => (doc.blocks.find((b) => b.id === id)?.config ?? {}) as Record<string, unknown>;

/** The document, read as toggles. Anything not recognisable falls to its default. */
function readSimple(doc: OneStopDocument | null): Simple {
  if (!doc || doc.blocks.length === 0) return DEFAULT_SIMPLE;
  const search = cfgOf(doc, "os-search");
  const coverage = Array.isArray(search.coverage) ? (search.coverage as string[]) : null;
  const inc = coverage
    ? { appt: coverage.includes("appointments"), walkin: coverage.includes("queues"), events: coverage.includes("events") }
    : { appt: has(doc, "os-appointments") || has(doc, "os-go-appts"), walkin: has(doc, "os-queues") || has(doc, "os-go-queue"), events: has(doc, "os-events") || has(doc, "os-featured") };
  const chips = Array.isArray(search.chips) ? (search.chips as unknown[]) : [];
  const featured = cfgOf(doc, "os-featured");
  return {
    /* The stored preset wins; a document saved before the preset was written reads from its hero banner. */
    layout: doc.settings?.layout === "site-landing" || has(doc, "os-hero") ? "l2" : "l1",
    inc,
    on: {
      signin: has(doc, "os-identity"),
      search: has(doc, "os-search"),
      pills: has(doc, "os-search") ? chips.length > 0 : true,
      walkins: has(doc, "os-queues") || !inc.walkin,
      appts: has(doc, "os-appointments") || !inc.appt,
      events: has(doc, "os-events") || !inc.events,
      directory: has(doc, "os-directory"),
      help: has(doc, "os-help"),
      featured: has(doc, "os-featured") || !inc.events,
      heroLinks: has(doc, "os-go-appts") || has(doc, "os-go-events") || has(doc, "os-go-queue") || !has(doc, "os-hero"),
    },
    featuredId: typeof featured.eventId === "string" && featured.autoPick !== true ? (featured.eventId as string) : null,
    signIn: ((cfgOf(doc, "os-identity").signedOut as Record<string, unknown> | undefined)?.method === "sso") ? "sso" : "password",
  };
}

interface Scope { services: string[]; queues: string[]; events: string[] }

/** Civil date YYYY-MM-DD in the tenant zone, offset by days. */
function civilDate(offsetDays: number, zone: string): string {
  const d = new Date(Date.now() + offsetDays * 86400000);
  try {
    const parts = new Intl.DateTimeFormat("en-CA", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
    return parts;
  } catch { return d.toISOString().slice(0, 10); }
}

/** Toggles → the document. Managed blocks in layout order, then everything else as it was. */
function buildDoc(simple: Simple, prev: OneStopDocument | null, args: { tenant: string; tenantName: string; timeZone: string; locale: string; scope: Scope; featuredAuto: string | null; journeySlugs: { appointment: string; queue: string; events: string }; showHeader?: boolean; showFooter?: boolean; journeyTarget?: "tab" | "same" }): OneStopDocument {
  const { tenant, tenantName, timeZone, locale, scope } = args;
  const C = copyFor(args.tenantName);
  const L = (text: string) => ({ [locale]: text });
  const keep = (id: string, fallback: Record<string, unknown>) => (prev && has(prev, id) ? { ...fallback, ...cfgOf(prev, id) } : fallback);
  const blk = (id: string, type: string, config: Record<string, unknown>): OneStopBlockInstance => ({ id, type, version: 1, config } as OneStopBlockInstance);

  const coverage = [simple.inc.walkin ? "queues" : null, simple.inc.appt ? "appointments" : null, simple.inc.events ? "events" : null].filter(Boolean);
  const managed: OneStopBlockInstance[] = [];
  const L2 = simple.layout === "l2";

  if (L2) {
    managed.push(blk("os-hero", "context-banner", { ...keep("os-hero", { text: L(C.headline), tone: "highlight" }), activeFrom: civilDate(-1, timeZone), activeTo: civilDate(365, timeZone) }));
  }
  if (simple.on.search) {
    const base = keep("os-search", { placeholder: L("Say it in your own words"), findHelpLabel: L("Find help"), maxChips: 6 });
    managed.push(blk("os-search", "search", { ...base, chips: simple.on.pills ? C.chips.map((c) => ({ label: L(c) })) : [], coverage }));
  }
  if (simple.on.signin) {
    /* signedOut is REWRITTEN each build, not kept: the operator removed the body line and
       the guest hint on all three tenants (2026-09-04), and a kept config would resurrect
       them from an earlier save. `signedOut.body` is registry-REQUIRED — omitting it is
       a publish refusal under the live validator; asked to be made optional in
       CD-API-REQUEST-onestop-simple-mode-2026-09-04.md §2.5. */
    const prevIdentity = keep("os-identity", {});
    managed.push(blk("os-identity", "identity-panel", {
      ...prevIdentity,
      /* `method` is not a registry key yet (asked in CD-API-REQUEST-portal-self-registration §3);
         it rides the config for the tab and is stripped in `forWire`. */
      signedOut: { eyebrow: L(C.signedOutEyebrow), title: L(C.signedOutTitle), buttonLabel: L("Sign in"), showGuestHint: false, method: simple.signIn },
      signedIn: { showNextAppointment: true, showLiveTicket: true, showHoldsSummary: true, ...((prevIdentity.signedIn as Record<string, unknown>) ?? {}) },
    }));
  }
  if (L2 && simple.on.heroLinks) {
    /* Each shortcut points at that journey's PUBLISHED page — /book/{slug}, the slug
       registered by the page designer on publish. Always rewritten (never `keep`),
       because a slug changes when the journey is republished under a new link. */
    const go = (id: string, label: string, slug: string) => blk(id, "button", { ...keep(id, { label: L(label), variant: "secondary" }), target: { kind: "deep-link", route: `/book/${slug}`, params: {} } });
    if (simple.inc.appt && simple.on.appts) managed.push(go("os-go-appts", C.shortcuts.appts, args.journeySlugs.appointment));
    if (simple.inc.events && simple.on.events) managed.push(go("os-go-events", C.shortcuts.events, args.journeySlugs.events));
    if (simple.inc.walkin && simple.on.walkins) managed.push(go("os-go-queue", C.shortcuts.queue, args.journeySlugs.queue));
  }
  const lists: OneStopBlockInstance[] = [];
  if (simple.inc.events && simple.on.events) lists.push(blk("os-events", "events", { ...keep("os-events", { maxItems: 4, showAllEventsLink: true }), scope: { events: scope.events } }));
  if (simple.inc.appt && simple.on.appts) lists.push(blk("os-appointments", "appointments", { ...keep("os-appointments", { maxItems: 3 }), scope: { services: scope.services } }));
  if (simple.inc.walkin && simple.on.walkins) lists.push(blk("os-queues", "queues", { ...keep("os-queues", { maxItems: 4, sort: "shortest-wait" }), scope: { queues: scope.queues } }));
  if (L2) {
    const eventId = simple.featuredId ?? args.featuredAuto;
    if (simple.inc.events && simple.on.featured && eventId) {
      /* `autoPick` is NOT a registry key — it rides the config so the tab can tell
         "auto" from "chosen" on reload. Unknown keys are refused at publish under
         locked strictness; it is stripped in `forWire`. */
      managed.push(blk("os-featured", "register-event", { label: L("Register now"), eventId, presentation: "inline", showCapacity: true, autoPick: simple.featuredId === null }));
    }
    managed.push(...lists); // events → appointments → queues, the site-landing order
  } else {
    managed.push(...lists.slice().reverse()); // queues → appointments → events, the get-help order
  }
  /* The directory is the tenant's ORG TREE (branches / departments / boutiques) on the live page;
     the preview resolves it from the tenant's locations, flat, and says so under the block. */
  if (simple.on.directory) managed.push(blk("os-directory", "directory", { ...keep("os-directory", { depth: 1, collapsedByDefault: true, showCapabilities: true }), title: L(C.directoryTitle), subtitle: L(C.directorySub) }));
  if (simple.on.help) {
    managed.push(blk("os-help", "help-contact", keep("os-help", {
      linkGroups: [{ title: L(C.helpTitle), links: C.helpLinks.map((l) => ({ label: L(l.label), target: { kind: "deep-link", route: l.route, params: {} } })) }],
    })));
  }
  const extras = prev ? prev.blocks.filter((b) => !MANAGED.has(b.id)) : [];
  return {
    title: prev?.title ?? `${tenantName} OneStop`,
    /* header.subtitle renders as the hero EYEBROW (uppercase, short) — the site-landing sub-copy is
       too long for it, so it is not written there. */
    header: { eyebrow: prev?.header.eyebrow ?? "OneStop", title: tenantName, subtitle: prev?.header.subtitle ?? null },
    blocks: [...managed, ...extras],
    /* `layout`/`showHeader`/`showFooter` are WRITTEN ON EVERY SAVE from the
       tab's own controls, over whatever the document already carried. They used to
       be copied from `prev.settings` unchanged once a page existed, so the Page
       layout and Header and footer controls changed the preview and never the live
       page (2026-09-16). Every other setting still comes from the stored document. */
    settings: {
      ...(prev?.settings ?? { navLabel: "OneStop", locale, supportedLocales: [locale], timeZone }),
      layout: L2 ? "site-landing" : "get-help-first",
      showHeader: args.showHeader ?? true,
      showFooter: args.showFooter ?? true,
      /* WHERE JOURNEYS OPEN (KP, 2026-09-17): written only once the author chose,
         so a strict settings schema is not handed a key it does not know yet
         (request filed). The portal reads it defensively; absent = new tab. */
      ...(args.journeyTarget ? { journeyTarget: args.journeyTarget } : {}),
    } as OneStopDocument["settings"],
  };
}

/** The document as the contract will accept it — tab-only keys stripped. */
function forWire(doc: OneStopDocument): OneStopDocument {
  return { ...doc, blocks: doc.blocks.map((b) => {
    const { autoPick, ...config } = (b.config ?? {}) as Record<string, unknown>; void autoPick;
    if (config.signedOut && typeof config.signedOut === "object") { const { method, ...so } = config.signedOut as Record<string, unknown>; void method; config.signedOut = so; }
    return { ...b, config } as OneStopBlockInstance;
  }) };
}

/* ── Preview seeds (overruled: numbers shown) ──────────────────────────── */
/* ONE seed, shared with the simulated join (`simulatedQueueDepth`), so a card
   saying "4 ahead" and the ticket that comes back from joining that desk cannot
   disagree — the join lands behind exactly the people the card showed. */
function seedWait(id: string): { wait: number; ahead: number } {
  const depth = simulatedQueueDepth(id);
  return { wait: depth.waitMinutes, ahead: depth.peopleAhead };
}

function withSeeds(blocks: OSPreviewBlock[]): OSPreviewBlock[] {
  return blocks.map((b) => {
    const data: any = b.data;
    if (b.type !== "queues" || !data || !Array.isArray(data.items)) return b;
    const items = data.items.map((q: any) => {
      const s = seedWait(String(q.queueId));
      /* busyLevel must be a `PortalBusyLevel` — `short-wait | busy | very-busy | closed`.
         An off-enum value misses the string catalogue (the chip printed a raw `busy.moderate`)
         and leaves BUSY_TONE undefined, so it loses its colour too. */
      const level = q.open === false ? "closed" : s.wait <= 10 ? "short-wait" : s.wait <= 18 ? "busy" : "very-busy";
      return { ...q, waitMinutes: s.wait, peopleAhead: s.ahead, busyLevel: level };
    });
    return { ...b, data: { ...data, items, asOf: new Date().toISOString() }, notice: b.notice ?? "Wait figures are preview seeds — the live page shows the queue's real numbers." };
  });
}

function zonedNow(zone: string, plusMinutes: number) {
  const d = new Date(Date.now() + plusMinutes * 60000);
  const date = civilDate(0, zone);
  const time = new Intl.DateTimeFormat("en-GB", { timeZone: zone, hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
  return { date, time, zone };
}

/** The identity fragment the signed-in preview attaches. Contract shape, seed values. */
function identityFragment(zone: string, name: string) {
  return { kind: "identity", greetingName: name, resolvedIdentity: null, holds: null, ticket: null, nextAppointment: zonedNow(zone, 26 * 60), degraded: false };
}

/** Consecutive hero-shortcut buttons (`os-go-*`) become one group; everything else is a group of one. */
function groupShortcuts(blocks: OneStopBlock[]): OneStopBlock[][] {
  const out: OneStopBlock[][] = [];
  for (const b of blocks) {
    const last = out[out.length - 1];
    if (b.id.startsWith("os-go-") && last && last[0]!.id.startsWith("os-go-")) last.push(b);
    else out.push([b]);
  }
  return out;
}

/** The shortcut row strips the Section padding each ButtonBlock brings, so three read as one line. */
const SHORTCUT_CSS = ".os-shortcuts .lv-section{padding:0 !important;margin:0 !important}.os-shortcuts .lv-section>*{margin:0 !important}.os-frame-wide .lv-hero{padding:26px 28px 28px;gap:14px}.os-frame-wide .lv-hero-grid{align-items:start;gap:20px;grid-template-columns:minmax(0,1fr) 296px}.os-frame-wide .lv-hero-grid>div>.lv-searchcard{margin-top:0}";

/* ── The tab ───────────────────────────────────────────────────────────── */
/** Tenant brand hex, per the seed-world workspace list — the fallback when the
 *  session's workspace carries none. The tint ramp is DERIVED from the one hex
 *  so header, hero and footer follow the brand instead of the renderer's maroon. */
/* BRAND_FALLBACK was a slug-keyed table of three real tenants' brand colours,
   so an unlisted tenant borrowed a named customer's brand (removed 2026-09-08).
   The session carries `brandColor`; when it does not, one neutral product
   default is honest about not knowing, where another company's maroon is not. */
const BRAND_FALLBACK = "#5142C7";
function tintFor(brand: string) {
  return {
    primary1: brand,
    primary2: `color-mix(in srgb, ${brand} 26%, #fff)`,
    primary3: `color-mix(in srgb, ${brand} 13%, #fff)`,
    primary4: `color-mix(in srgb, ${brand} 6%, #fff)`,
    primaryText: `color-mix(in srgb, ${brand} 82%, #000)`,
  };
}

export interface OneStopTabProps {
  tenant: string;
  tenantName?: string;
  brandColor?: string;
  publicHost?: string;
  journeyOptions: { value: string; label: string }[];
  onSwitchJourney: (j: string) => void;
}

type Load<T> = { status: "loading" } | { status: "ready"; value: T } | { status: "error"; message: string };

export function OneStopTab(props: OneStopTabProps): JSX.Element {
  const { tenant } = props;
  const tenantName = props.tenantName ?? tenant;
  const C = copyFor(props.tenantName);
  /* Was a `a tenant-slug-based noun selection` ternary
     (removed 2026-09-08). A three-way on real tenant slugs is the same defect as
     a slug-keyed table with fewer characters, and its else-branch shipped one
     vertical's noun to every other tenant. The served vocabulary answers it
     where a workspace has set one — but the vocabulary carries the CUSTOMER
     noun, not the staff noun, so there is nothing to read yet and inventing a
     read that returns the same value either way would be worse than none. One
     neutral word until the vocabulary carries `staff`, raised with the API
     line. */
  const staffNoun = "specialist";
  const tint = useMemo(() => tintFor(props.brandColor || BRAND_FALLBACK), [props.brandColor]);
  const zone = tzContext().zone;
  const locale = "en-US";
  const [page, setPage] = useState<Load<{ doc: OneStopDocument | null; published: boolean; publishedAt: string | null }>>({ status: "loading" });
  const [simple, setSimple] = useState<Simple>(DEFAULT_SIMPLE);
  const [dirty, setDirty] = useState(false);
  const [saveNote, setSaveNote] = useState<"saved" | "published" | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [who, setWho] = useState<"guest" | "customer">("guest");
  const [sso, setSso] = useState(false);
  const [pwEmail, setPwEmail] = useState("");
  const [pwPass, setPwPass] = useState("");
  const [pwNote, setPwNote] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const [previewOnly, setPreviewOnly] = useState(false);
  /* TRY THE FLOWS — on by default (operator, 2026-09-04).

     The preview used to install `unavailableActions`, so every Join / Book /
     Register rendered greyed with "Not live in preview" and an operator could
     not answer the only question they were asking: what does a customer see
     after they tap it? `simulatedPortalActions` runs all three flows in memory
     — same interface, same required fields, same refusals, same latency — and
     reaches no server: nothing is joined, held, booked, registered or saved.
     Off puts the honest disabled state back, which is what the live page shows
     a preview host that cannot fire anything.

     `simEpoch` is the reset. It keys the provider, so bumping it remounts the
     block subtree and drops every ticket, hold and confirmation the operator
     accumulated — the flows start from their first frame again. */
  const [simulate, setSimulate] = useState(true);
  /* The last handoff, for the readout under the frame. An operator checking
     "does the service carry over" should not have to open devtools or read a
     new tab's address bar to see the answer. */
  const [lastHandoff, setLastHandoff] = useState<{ label: string; url: string } | null>(null);
  const [simEpoch, setSimEpoch] = useState(0);
  const [section, setSection] = useState<"general" | "hero" | "sections" | "launch">("general");
  const [step, setStep] = useState<"home" | "results" | "signedin" | "sso">("home");
  const [device, setDevice] = useState<"desktop" | "tablet" | "mobile">("desktop");
  const [formOpen, setFormOpen] = useState(true);
  /* Three layouts, the same rule the Appointments shell uses. Below the
     three-pane width only one of settings/preview is on screen and the header
     carries the switch; below the rail width the setup rail collapses into a
     picker at the top of the settings pane. */
  const layout = useDesignerLayout();
  const onePane = layout !== "wide";
  const phone = layout === "phone";
  /* Header / footer on-off, matching the Appointments journey's "Header and footer" section.
     They are the document's own `settings.showHeader` / `settings.showFooter`: hydrated from
     the stored page when it loads and written by `buildDoc` on save. They used to live in
     localStorage, so a fresh browser always showed both on and the live page never changed. */
  const [chrome, setChrome] = useState<{ header: boolean; footer: boolean }>({ header: true, footer: true });
  const setChromeKey = (k: "header" | "footer") => (v: boolean) => { setChrome((c) => ({ ...c, [k]: v })); setDirty(true); setSaveNote(null); };
  /* Where the three journeys open from this page (KP, 2026-09-17): a new tab (default) or
     this tab. Either way the journey shows a "Back to <page>" strip on every step — only
     when launched from here. Hydrated from `settings.journeyTarget`, written by buildDoc. */
  const [journeyTarget, setJourneyTargetState] = useState<"tab" | "same">("tab");
  const setJourneyTarget = (v: "tab" | "same") => { setJourneyTargetState(v); setDirty(true); setSaveNote(null); };
  const [embed, setEmbed] = useState<{ unit: "auto" | "px"; width: number; resize: "auto" | "fixed"; height: number }>({ unit: "auto", width: 960, resize: "auto", height: 900 });
  const [copiedEmbed, setCopiedEmbed] = useState(false);
  /** The slug of the document the portal serves at its root, once resolved.
   *
   *  DECLARED HERE, ABOVE `embedCode`, and the order is load-bearing: the IIFE
   *  below reads `rootSlug` on every render, and the declaration used to sit ~40
   *  lines further down among the data hooks. That is a temporal-dead-zone
   *  reference — a hard `ReferenceError` at runtime and an error under the kit's
   *  strict config, surviving here only because the preview bundles without
   *  typechecking (2026-09-08). Keep it above its first reader. */
  const [rootSlug, setRootSlug] = useState<string | null>(null);
  const publicConfig = useTenantPublicConfig();
  const liveUrl = publicConfig?.rootDomain ? `https://${tenantPublicHost("onestop", tenant)}/` : "";
  const embedCode = (() => {
    if (!liveUrl) return "";
    const auto = embed.resize === "auto";
    const w = embed.unit === "px" ? String(embed.width) : "100%";
    if (auto) return [
      `<script src="${liveUrl}embed/v1.js" defer><\/script>`,
      `<jrni-one-stop style="width:${embed.unit === "px" ? `${embed.width}px` : "100%"};max-width:100%"></jrni-one-stop>`,
    ].join("\n");
    const L = ["<iframe", `  src="${liveUrl}"`, `  width="${w}"`, `  height="${embed.height}"`, '  style="border:0"'];
    L.push(`  title="OneStop \u2014 ${tenantName}"`);
    L.push('  allow="geolocation; payment; clipboard-write; camera"');
    L.push('  referrerpolicy="strict-origin-when-cross-origin"');
    L.push('  sandbox="allow-scripts allow-forms allow-popups allow-same-origin allow-top-navigation-by-user-activation"');
    L.push('  loading="lazy">', "</iframe>");
    return L.join("\n");
  })();
  const copyEmbed = () => { try { if (navigator.clipboard) navigator.clipboard.writeText(embedCode); } catch { /* clipboard unavailable */ } setCopiedEmbed(true); setTimeout(() => setCopiedEmbed(false), 1600); };
  /* The renderer's mobile rules live behind @media (max-width: 640px), which reads the
     VIEWPORT — a 390 px frame inside a wide designer would still get the desktop grid. The
     same rules are re-emitted here scoped to the phone frame, verbatim from the sheet the
     theme injects, so the phone preview is the renderer's own phone layout. */
  const [mobileCss, setMobileCss] = useState("");
  useEffect(() => {
    const node = document.getElementById("lv-one-stop-styles");
    const text = node?.textContent ?? "";
    const m = text.match(/@media \(max-width: 640px\) \{([\s\S]*?)\n\}/);
    if (!m) return;
    const scoped = m[1]!.replace(/(^|\n)\s*([^@{}\n][^{]*?)\s*\{/g, (_all, lead: string, sel: string) => lead + sel.split(",").map((s) => ".os-frame-mobile " + s.trim()).join(", ") + " {");
    setMobileCss(scoped);
  }, [page.status]);
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [queues, setQueues] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  /* THE PREVIEW DOES NOT GATE THE LIST SECTIONS ON JOURNEY PUBLICATION, and the
     comment below is the reason — the per-journey
     "published" state that dimmed sections and badged them "Hidden until that
     journey is published" is gone. It was answered from a local stub
     (`listPageDesigner` reads localStorage), so every fresh browser read every
     journey as unpublished — and the live resolver does not gate list blocks on
     journey publication at all, so the badge claimed a gate the live page does
     not have. The slug half of that read stays: it names the hand-off targets. */
  const [journeySlugs, setJourneySlugs] = useState<{ appointment: string; queue: string; events: string }>({ appointment: "appointment", queue: "queue", events: "events" });
  /* LOCAL PATCH (platform side, 2026-09-15 preview hand-off destinations, JAI-1646).
     The hand-off URLs the live page would put on the cards this draft selects,
     read from the server that composes them — never composed here. Keyed by
     the ids the draft scopes; re-read when that set changes, not on every
     render. A failed read leaves the maps empty and every card renders as it
     did before this read existed. Search results are overlaid from the same
     maps, so a hit outside the draft's scope carries no URL. */
  const [handoffUrls, setHandoffUrls] = useState<OneStopHandoffDestinations>({ queues: {}, services: {}, events: {} });
  const { events } = useEvents();
  /* The directory fragment resolves from the tenant's real locations (onestop-preview-data
     §directoryFragment) — one level deep, capability chips derived from its queues, services
     and events, with the block's own notice saying what the live org tree will differ on. */
  const { locations } = useLocations();
  const { members: staffMembers } = useStaff();
  const ssoTimer = useRef<any>(null);

  useEffect(() => {
    let live = true;
    /* Resolve the root document FIRST; every read and write below is against the
       slug that comes back, never against a name this screen chose. */
    const loadRoot = (slug: string) => getOneStopPage(slug)
      .then((p) => { if (!live) return; const doc = { title: p.title, header: p.header, blocks: p.blocks, settings: p.settings } as OneStopDocument; setPage({ status: "ready", value: { doc, published: !!p.published, publishedAt: p.publishedAt ?? null } }); setSimple(readSimple(doc)); setChrome({ header: p.settings?.showHeader !== false, footer: p.settings?.showFooter !== false }); setJourneyTargetState(((p.settings as { journeyTarget?: unknown } | undefined)?.journeyTarget === "same") ? "same" : "tab"); })
      .catch((err: unknown) => { if (!live) return; if ((err as any)?.status === 404) { setPage({ status: "ready", value: { doc: null, published: false, publishedAt: null } }); setSimple({ ...DEFAULT_SIMPLE, signIn: SIGNIN_FALLBACK }); } else setPage({ status: "error", message: String((err as any)?.message ?? err) }); });
    resolvePortalRootSlug()
      .then((slug) => {
        if (!live) return;
        if (!slug) { setRootSlug(null); setPage({ status: "ready", value: { doc: null, published: false, publishedAt: null } }); setSimple({ ...DEFAULT_SIMPLE, signIn: SIGNIN_FALLBACK }); return; }
        setRootSlug(slug);
        loadRoot(slug);
      })
      .catch((err) => { if (live) setPage({ status: "error", message: String((err as any)?.message ?? err) }); });
    listQueues().then((p: any) => { if (live) setQueues(p?.data ?? []); }).catch(() => {});
    getServiceCatalogue().then((c) => { if (live) setServices(((c?.services ?? []) as any[]).filter((s) => !s.archived && s.bookedBy !== "advisor")); }).catch(() => {});
    listPageDesigner(tenant).then((env) => {
      if (!live) return;
      const slugOf = (j: "appointment" | "queue" | "events") => env.records[j]?.slug || j;
      setJourneySlugs({ appointment: slugOf("appointment"), queue: slugOf("queue"), events: slugOf("events") });
    }).catch(() => {});
    return () => { live = false; clearTimeout(ssoTimer.current); };
  }, [tenant]);

  const scope: Scope = useMemo(() => ({
    services: services.map((s) => String(s.id)),
    queues: queues.map((q) => String(q.id)),
    events: (events ?? []).map((e) => String(e.id)),
  }), [services, queues, events]);

  const featuredAuto = useMemo(() => {
    const rows = (events ?? []).slice().sort((a: any, b: any) => (b.registered ?? 0) - (a.registered ?? 0));
    return rows[0] ? String(rows[0].id) : null;
  }, [events]);

  const prevDoc = page.status === "ready" ? page.value.doc : null;
  const doc = useMemo(() => buildDoc(simple, prevDoc, { tenant, tenantName, timeZone: zone, locale, scope, featuredAuto, journeySlugs, showHeader: chrome.header, showFooter: chrome.footer, journeyTarget }), [simple, prevDoc, tenant, tenantName, zone, scope, featuredAuto, journeySlugs, chrome.header, chrome.footer, journeyTarget]);
  const extras = doc.blocks.filter((b) => !MANAGED.has(b.id)).length;

  const patch = (fn: (s: Simple) => Simple) => { setSimple((s) => fn(s)); setDirty(true); setSaveNote(null); };
  const setOn = (k: keyof Simple["on"]) => (v: boolean) => patch((s) => ({ ...s, on: { ...s.on, [k]: v } }));
  const setInc = (k: keyof Simple["inc"]) => (v: boolean) => patch((s) => ({ ...s, inc: { ...s.inc, [k]: v } }));

  /* A tenant with no experience documents yet gets one created at the fallback
     name on its first save — the same door the portal serves from. */
  const writeSlug = rootSlug ?? ONESTOP_SLUG_FALLBACK;

  /* Save-only path retained for the draft door (saveOneStopDraft) even though the header no
     longer offers it: `publish` saves then publishes, which is the only user-facing write now. */
  const saveDraftOnly = useCallback(async () => {
    setBusy(true); setProblem(null);
    try {
      const saved = await saveOneStopDraft(writeSlug, forWire(doc));
      setPage({ status: "ready", value: { doc: { title: saved.title, header: saved.header, blocks: saved.blocks, settings: saved.settings } as OneStopDocument, published: !!saved.published, publishedAt: saved.publishedAt ?? null } });
      setDirty(false); setSaveNote("saved");
    } catch (err) { setProblem(String((err as any)?.message ?? err)); }
    finally { setBusy(false); }
  }, [doc, writeSlug]);
  void saveDraftOnly;

  const publish = useCallback(async () => {
    setBusy(true); setProblem(null);
    try {
      await saveOneStopDraft(writeSlug, forWire(doc));
      const pub = await publishOneStopPage(writeSlug);
      setPage({ status: "ready", value: { doc: { title: pub.title, header: pub.header, blocks: pub.blocks, settings: pub.settings } as OneStopDocument, published: true, publishedAt: pub.publishedAt ?? new Date().toISOString() } });
      setDirty(false); setSaveNote("published");
    } catch (err) { setProblem(String((err as any)?.message ?? err)); }
    finally { setBusy(false); }
  }, [doc, writeSlug]);

  /* ── preview data ── */
  const uidBlocks = doc.blocks.map((b) => ({ uid: b.id, id: b.id, type: b.type, version: b.version ?? 1, config: (b.config ?? {}) as Record<string, unknown>, visibility: (b as any).visibility }));
  const rendered = useMemo(() => withSeeds(withPreviewData(uidBlocks, { queues: queues as never, services: services as never, events: (events ?? []) as never, locations: (locations ?? []) as never, staff: (staffMembers ?? []) as never }, zone)), [doc, queues, services, events, locations, zone]);
  // LOCAL PATCH (platform side, 2026-09-15 preview hand-off destinations, JAI-1646)
  const handoffIdsKey = useMemo(() => {
    const ids = { queues: new Set<string>(), services: new Set<string>(), events: new Set<string>() };
    for (const b of rendered) {
      const items = (b.data as any)?.items as any[] | undefined;
      if (!items) continue;
      for (const it of items) {
        if (b.type === "queues" && it.queueId) ids.queues.add(String(it.queueId));
        if (b.type === "appointments" && it.serviceId) ids.services.add(String(it.serviceId));
        if (b.type === "events" && it.eventId) ids.events.add(String(it.eventId));
      }
    }
    return JSON.stringify({ queues: [...ids.queues].sort(), services: [...ids.services].sort(), events: [...ids.events].sort() });
  }, [rendered]);
  useEffect(() => {
    let live = true;
    const ids = JSON.parse(handoffIdsKey) as { queues: string[]; services: string[]; events: string[] };
    resolveOneStopHandoffDestinations(ids)
      .then((d) => { if (live) setHandoffUrls(d); })
      .catch(() => { if (live) setHandoffUrls({ queues: {}, services: {}, events: {} }); });
    return () => { live = false; };
  }, [handoffIdsKey]);
  /* Lays the server's destinations onto the fragments the preview builder made.
     `null` stays null: the queue and event renderers gate the hand-off button
     on a navigable URL and fall back to the inline flow otherwise; the
     appointment renderer keeps its composed-slug hand-off for a null — each
     exactly as it behaves on the live page. */
  const withHandoffUrls = useCallback(<T extends { type: string; data?: unknown }>(blocks: T[]): T[] => blocks.map((b) => {
    const data = b.data as { items?: any[] } | undefined;
    if (!data?.items) return b;
    const items = data.items.map((it) => {
      if (b.type === "queues" && it.queueId) return { ...it, joinUrl: handoffUrls.queues[String(it.queueId)] ?? null };
      if (b.type === "appointments" && it.serviceId) return { ...it, bookUrl: handoffUrls.services[String(it.serviceId)] ?? null };
      if (b.type === "events" && it.eventId) return { ...it, registerUrl: handoffUrls.events[String(it.eventId)] ?? null };
      return it;
    });
    return { ...b, data: { ...data, items } };
  }), [handoffUrls]);

  const signedIn = who === "customer";
  const previewBlocks: OneStopBlock[] = withHandoffUrls(rendered).map((b) => { // LOCAL PATCH (platform side, 2026-09-15 preview hand-off destinations, JAI-1646): withHandoffUrls
    const out: any = { id: b.id, type: b.type, version: b.version, config: b.config, visibility: b.visibility };
    if (b.data) out.data = b.data;
    if (b.type === "identity-panel" && signedIn) out.data = identityFragment(zone, "Dana");
    return out as OneStopBlock;
  });

  /* ── preview search (client-side, preview only) ── */
  const q = submitted.trim().toLowerCase();
  const words = q.split(/\s+/).filter((w) => w.length > 2);
  const hit = (hay: string) => q.length > 0 && (hay.includes(q) || words.some((w) => hay.includes(w)));
  const searchBlocks: OneStopBlock[] = useMemo(() => {
    if (!q) return [];
    const cov = new Set(Array.isArray(cfgOf(doc, "os-search").coverage) ? (cfgOf(doc, "os-search").coverage as string[]) : []);
    const svc = services.filter((s) => hit(`${s.name ?? ""} ${s.description ?? ""} ${s.lob ?? ""}`.toLowerCase())).map((s) => String(s.id));
    const qs = queues.filter((x) => hit(`${x.name ?? ""} ${x.locationName ?? ""}`.toLowerCase())).map((x) => String(x.id));
    const evs = (events ?? []).filter((e: any) => hit(`${e.title ?? ""} ${e.description ?? ""} ${e.template ?? ""}`.toLowerCase())).map((e: any) => String(e.id));
    const found: any[] = [];
    if (cov.has("queues") && qs.length) found.push({ uid: "r-q", id: "r-q", type: "queues", version: 1, config: { scope: { queues: qs } } });
    if (cov.has("appointments") && svc.length) found.push({ uid: "r-a", id: "r-a", type: "appointments", version: 1, config: { scope: { services: svc } } });
    if (cov.has("events") && evs.length) found.push({ uid: "r-e", id: "r-e", type: "events", version: 1, config: { scope: { events: evs } } });
    return withHandoffUrls(withSeeds(withPreviewData(found, { queues: queues as never, services: services as never, events: (events ?? []) as never, locations: (locations ?? []) as never, staff: (staffMembers ?? []) as never }, zone))).map((b) => ({ id: b.id, type: b.type, version: b.version, config: b.config, data: b.data } as unknown as OneStopBlock)); // LOCAL PATCH (platform side, 2026-09-15 preview hand-off destinations, JAI-1646): withHandoffUrls
  }, [q, doc, services, queues, events, locations, staffMembers, zone, withHandoffUrls]);

  const search: SearchController = {
    value: query, status: q ? "done" : "idle", errorMessage: null,
    onChange: (v) => { setQuery(v); if (!v) setSubmitted(""); },
    onSubmit: (v) => { setQuery(v); setSubmitted(v); },
    onClear: () => { setQuery(""); setSubmitted(""); },
  };

  const HERO = new Set(["context-banner", "search", "identity-panel"]);
  const heroEnd = (() => { let e = 0; while (e < previewBlocks.length && HERO.has(previewBlocks[e]!.type)) e += 1; return previewBlocks.slice(0, e).some((b) => b.type === "search" || b.type === "identity-panel") ? e : 0; })();
  const heroBlocks = previewBlocks.slice(0, heroEnd);
  const bodyBlocks = previewBlocks.slice(heroEnd);
  const trailing = bodyBlocks.filter((b) => b.type === "help-contact");
  const middle = bodyBlocks.filter((b) => b.type !== "help-contact");

  const sessionState = {
    session: signedIn ? { sessionId: "preview", displayName: "Dana Reyes", demo: true, contact: { givenName: "Dana", familyName: "Reyes", email: "dana.reyes@example.com", phone: null } } : null,
    canSignIn: true,
    onSignedIn: () => setWho("customer"),
    onSignOut: () => setWho("guest"),
    notice: null as null,
  };
  const startSso = () => { setSso(true); setStep("sso"); clearTimeout(ssoTimer.current); ssoTimer.current = setTimeout(() => { setSso(false); setWho("customer"); setStep("signedin"); }, 1100); };
  const goStep = (s: typeof step) => {
    clearTimeout(ssoTimer.current);
    setStep(s);
    if (s === "home") { setSso(false); setWho("guest"); setQuery(""); setSubmitted(""); }
    if (s === "results") { setSso(false); setWho("guest"); const first = C.chips[0] ?? ""; setQuery(first); setSubmitted(first); }
    if (s === "signedin") { setSso(false); setWho("customer"); setQuery(""); setSubmitted(""); }
    if (s === "sso") { setWho("guest"); setQuery(""); setSubmitted(""); setSso(true); }
  };
  /* One instance per (mode, zone, reset) — memoized because its whole state (the
     entries, holds and durations it invents) lives in the closure, so a new
     instance every render would forget a ticket the moment the operator typed. */
  /* THE CTAs HAND OFF. Book, Join and Register open the journey that owns the
     flow in a NEW TAB, carrying what was tapped — the service for an
     appointment row, the desk for a walk-in card, the event for a registration
     (operator ruling, 2026-09-04). The URL is the journey's PUBLISHED customer
     URL on this tenant's public host, built from the slug the page designer
     registered on publish; it is the same link the hero shortcuts already use,
     with the chosen id on the query string.

     The simulated implementation stays underneath it. Handoff covers the three
     flows that have a journey to open; anything else a block can fire (a
     position poll on a ticket already on screen, a hold released on cancel)
     still runs in memory rather than reaching a server from a builder. */
  const previewActions = useMemo(() => {
    const base = simulate ? simulatedPortalActions({ timeZone: zone }) : unavailableActions("");
    if (!simulate) return base;
    return {
      ...base,
      handoff: (target: OneStopHandoffTarget) => {
        const url = handoffUrl(target, journeySlugs, `https://${props.publicHost ?? tenantPublicHost("book", tenant)}`);
        setLastHandoff({ label: target.label || target.kind, url });
        /* ⛔ NOT `window.open(url, "_blank", "noopener,noreferrer")`. The HTML spec
           makes `window.open` return null whenever the features carry `noopener`, even
           when the tab opened fine, so the readout said "(popup blocked)" on every
           hand-off. Severing `opener` on the returned handle fixed that but dropped
           `noreferrer` with it, and this preview hands off to the tenant's booking host.

           An anchor click keeps BOTH: `rel="noopener noreferrer"` denies the opened tab a
           handle and suppresses the referrer, and a link activation inside this click's
           user gesture is not what popup blockers refuse — so there is no null handle to
           mis-read and no "(popup blocked)" branch to get wrong. The readout below the
           frame shows the URL the tap resolved to either way, which is what the operator
           was checking. (`blocks/one-stop/actions.tsx` keeps `window.open`: it needs the
           handle for its top-frame fallback on a kiosk.) */
        const a = document.createElement("a");
        a.href = url; a.target = "_blank"; a.rel = "noopener noreferrer"; a.style.display = "none";
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
      },
    };
  }, [simulate, zone, simEpoch, journeySlugs, props.publicHost, tenant]);
  const frameWidth = previewOnly ? "100%" : device === "mobile" ? "390px" : device === "tablet" ? "min(100%, 768px)" : "100%";
  const PANELS: Record<typeof section, [string, string]> = {
    general: ["General", "Which layout the page uses, what customers can do from it, and how they sign in."],
    hero: ["Search & hero", "The search card, its quick questions, and — on the site-landing layout — the shortcuts and featured event."],
    sections: ["Page sections", "Each section on or off, in the order it appears."],
    launch: ["Launch & embed", "Where the published page answers, and the iframe to put it on your own site."],
  };
  const setupItems = (["general", "hero", "sections", "launch"] as const).map((id) => ({ id, label: PANELS[id][0] }));
  const journeyItems: { id: typeof step; num: string; label: string; warn?: boolean }[] = [
    { id: "home", num: "1", label: "Home" },
    ...(simple.on.search ? [{ id: "results" as const, num: "2", label: "Search results" }] : []),
    ...(simple.on.signin ? [{ id: "signedin" as const, num: simple.on.search ? "3" : "2", label: "Signed in" }, ...(simple.signIn === "sso" ? [{ id: "sso" as const, num: "!", label: "SSO handoff", warn: true }] : [])] : []),
  ];

  const published = page.status === "ready" && page.value.published;
  // Keep the single save-and-publish action without claiming an unpublished
  // or unread document is already live.
  const statusLabel = page.status === "loading" ? "Loading" : page.status === "error" ? "Unable to load" : dirty ? "Unsaved changes" : published ? "Published" : "Not published";
  const statusColor = published && !dirty ? "green" : "orange";
  const edge = `var(--jrni-color-semantic-${statusColor}-1)`;
  const fill = `var(--jrni-color-semantic-${statusColor}-2)`;
  const ctx = { locale, timeZone: zone, host: "builder" as const, search };

  const eventOptions = [{ value: "", label: `Auto — most registrations${featuredAuto ? "" : " (no events yet)"}` }, ...(events ?? []).map((e: any) => ({ value: String(e.id), label: String(e.title ?? e.id) }))];

  return (
    <div style={{ height: "100%", flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", background: "var(--jrni-color-surface-canvas)", fontFamily: "var(--jrni-font-family-sans)", color: "var(--jrni-color-text-strong)" }}>
      <style>{TABSCROLL_CSS}</style>
      <style>{mobileCss}</style>
      <style>{SHORTCUT_CSS}</style>
      {!previewOnly ? (
        <header style={{ flex: "0 0 auto", display: "flex", alignItems: "center", flexWrap: "wrap", gap: "14px", rowGap: "8px", padding: "10px 16px", background: "var(--jrni-color-surface-card)", borderBottom: "1px solid var(--jrni-color-surface-border)", boxShadow: "var(--jrni-shadow-chrome)", zIndex: 5 }}>
          {/* The tab strip scrolls rather than wrapping, so the six journey
              tabs keep their labels at every width. */}
          <div style={{ flex: "1 1 auto", minWidth: 0, display: "flex", alignItems: "center", gap: "10px" }}>
            <div className="apd-tabscroll" style={{ flex: "0 1 auto", minWidth: 0, padding: "2px 0" }}>
              <SegmentedControl options={props.journeyOptions} value="onestop" onChange={(v: any) => props.onSwitchJourney(v)} size="sm" aria-label="Journey type" />
            </div>
            <span style={{ flex: "0 0 auto", display: "inline-flex", alignItems: "center", gap: "6px", padding: "3px 9px", borderRadius: "999px", fontSize: "12px", fontWeight: 600, border: `1px solid ${edge}`, background: fill, color: edge }}>
              <span style={{ width: "6px", height: "6px", borderRadius: "999px", background: edge }}></span>{statusLabel}
            </span>
          </div>
          <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: "8px", whiteSpace: "nowrap" }}>
            {onePane ? (
              <SegmentedControl
                options={[{ value: "settings", label: "Settings" }, { value: "preview", label: "Preview" }]}
                value={formOpen ? "settings" : "preview"}
                onChange={(v: any) => setFormOpen(v !== "preview")}
                size="sm"
                aria-label="Show settings or preview"
              />
            ) : null}
            <Button variant="ghost" size="sm" iconRight={(UI as any).IconExternal ? React.createElement((UI as any).IconExternal, { size: 13 }) : undefined} onClick={() => setPreviewOnly(true)}>Preview</Button>
            {dirty || (page.status === "ready" && !published) ? <Button hierarchy="primary" size="sm" onClick={publish} disabled={busy || page.status !== "ready"}>{busy ? "Saving…" : "Save & publish"}</Button> : null}
          </div>
          {saveNote || problem ? (
            <div style={{ flex: "0 0 100%", display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: problem ? "var(--jrni-color-semantic-red-1)" : "var(--jrni-color-text-soft)" }}>
              <span style={{ width: "6px", height: "6px", borderRadius: "999px", background: problem ? "var(--jrni-color-semantic-red-1)" : saveNote === "published" ? "var(--jrni-color-semantic-green-1)" : "var(--jrni-color-neutral-3)" }}></span>
              <span>{problem ? problem : saveNote === "published" ? "Saved & published — this page is live for customers at" : "All changes saved and live for customers."}</span>
              {!problem && saveNote === "published" ? <Button variant="link" size="sm" onClick={() => setPreviewOnly(true)}>{liveUrl}</Button> : null}
            </div>
          ) : null}
        </header>
      ) : (
        <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 10, padding: "8px 16px", background: "var(--jrni-color-surface-card)", borderBottom: "1px solid var(--jrni-color-surface-border)" }}>
          <Button variant="ghost" size="sm" onClick={() => setPreviewOnly(false)}>← Back to editor</Button>
          <span style={{ fontSize: 12, color: "var(--jrni-color-text-soft)" }}>{liveUrl}</span>
          <div style={{ flex: "1 1 auto" }}></div>
          {simulate ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 11, color: "var(--jrni-color-text-soft)" }}>
              Book / Join / Register open the journey in a new tab
              <Button variant="ghost" size="sm" onClick={() => setSimEpoch((e) => e + 1)}>Start over</Button>
            </span>
          ) : null}
        </div>
      )}

      <div className="apd-scroll" style={{ flex: "1 1 auto", display: "flex", minHeight: 0, minWidth: 0, overflow: "hidden" }}>
        {!previewOnly && !phone ? (
          <nav className="apd-scroll" style={{ flex: "0 0 236px", width: 236, minWidth: 236, boxSizing: "border-box", overflowY: "auto", overflowX: "hidden", background: "var(--jrni-color-surface-card)", borderRight: "1px solid var(--jrni-color-surface-border)", padding: "16px 13px 20px", display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ padding: "0 13px 2px" }}><SectionLabel>Setup</SectionLabel></div>
            <EditorSectionNav items={setupItems as any} ariaLabel="Setup" value={section} onChange={(id: any) => { setSection(id); setFormOpen(true); }} width={210} />
            <div style={{ padding: "14px 13px 2px" }}><SectionLabel>Customer journey</SectionLabel></div>
            <div role="tablist" aria-label="Journey steps" style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {journeyItems.map((j) => {
                const selected = step === j.id;
                return (
                  <button key={j.id} type="button" role="tab" aria-selected={selected} onClick={() => goStep(j.id)} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box", textAlign: "left", padding: "8px 12px", borderRadius: "var(--jrni-radius-control)", border: `1px solid ${selected ? "var(--jrni-color-surface-border)" : "transparent"}`, cursor: "pointer", fontFamily: "inherit", fontSize: 14, background: selected ? "var(--jrni-color-surface-card)" : "transparent", boxShadow: selected ? "var(--jrni-shadow-sm)" : "none", color: selected ? "var(--jrni-color-text-strong)" : "var(--jrni-color-text-body)" }}>
                    <span style={{ flex: "0 0 22px", height: 22, borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, background: selected ? "var(--jrni-color-primary-1)" : "var(--jrni-color-neutral-6)", color: selected ? "#FFFFFF" : j.warn ? "var(--jrni-color-semantic-orange-1)" : "var(--jrni-color-text-soft)" }}>{j.num}</span>
                    <span style={{ flex: "1 1 auto", fontWeight: 500, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{j.label}</span>
                  </button>
                );
              })}
            </div>
          </nav>
        ) : null}

        {!previewOnly && formOpen ? (
          <div className="apd-scroll" style={{ flex: "1 1 520px", minWidth: 0, overflow: "auto", padding: phone ? "16px 16px 48px" : "24px 28px 56px", background: "var(--jrni-color-surface-canvas)" }}>
            {phone ? (
              <div style={{ marginBottom: 16 }}>
                <Field label="Section">
                  <Dropdown
                    options={setupItems.map((i: any) => ({ value: "s:" + i.id, label: i.label }))
                      .concat(journeyItems.map((j: any) => ({ value: "j:" + j.id, label: j.label })))}
                    value={journeyItems.some((j: any) => j.id === step) && !setupItems.some((i: any) => i.id === section) ? "j:" + step : "s:" + section}
                    onChange={(v: any) => {
                      const id = String(v).slice(2);
                      if (String(v).charAt(0) === "j") goStep(id as any);
                      else { setSection(id as any); setFormOpen(true); }
                    }}
                  />
                </Field>
              </div>
            ) : null}
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 20, maxWidth: 880 }}>
              <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                <h1 style={{ margin: 0, fontSize: phone ? 18 : 22, fontWeight: 700, letterSpacing: "-0.01em" }}>{PANELS[section][0]}</h1>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 880 }}>
              {section === "general" ? (<>
                <Section title="Layout" description={simple.layout === "l1" ? "Search and sign-in lead. Everything else is a shortlist underneath." : "Reads like the rest of your site: headline, shortcuts, a featured event, then events, booking and the queue."}>
                  <Field label="Page layout">{/* ui-config (LOCAL PATCH, platform side, 2026-09-08 full resync: layout options, not data) */}
                    <SegmentedControl options={[{ value: "l1", label: "1 · Get help first" }, { value: "l2", label: "2 · Site landing" }]} value={simple.layout} onChange={(v: any) => patch((s) => ({ ...s, layout: v }))} size="sm" aria-label="Layout" />
                  </Field>
                </Section>
                <Section title="Header and footer">
                  <ToggleRow title="Show custom header" description="Logo and title bar above the page." checked={chrome.header} onChange={setChromeKey("header")} />
                  <ToggleRow title="Show custom footer" description="Contact links and legal text below the page." checked={chrome.footer} onChange={setChromeKey("footer")} />
                </Section>
                <Section title="Where journeys open" description="Book, Join the line and Register hand the customer to the journey that owns the flow. Either way, every page of that journey shows a way back here — only when it was opened from this page.">
                  <Field label="Open journeys in" hint={journeyTarget === "same" ? "The booking takes over this tab. “Back to OneStop” returns here." : "This page stays open behind the booking. “Back to OneStop” closes the booking tab."}>
                    <SegmentedControl options={[{ value: "tab", label: "A new tab" }, { value: "same", label: "This tab" }]} value={journeyTarget} onChange={(v: any) => setJourneyTarget(v === "same" ? "same" : "tab")} />
                  </Field>
                  {/* What the customer sees at the top of every journey page opened from
                      here — the same component the public pages mount, inert. */}
                  <div style={{ border: "1px solid var(--jrni-color-surface-border)", borderRadius: 8, overflow: "hidden", background: "var(--jrni-color-base-white)" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)", padding: "7px 12px 5px", background: "var(--jrni-color-neutral-6)", borderBottom: "1px solid var(--jrni-color-surface-border)" }}>Preview · top of a booking page opened from here</div>
                    <ReturnToOneStopBar preview={{ fromLabel: doc.header.title || tenantName || "OneStop" }} />
                    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: "var(--jrni-color-primary-1)" }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#FFFFFF" }}>Booking page</span>
                      <span style={{ flex: 1 }} />
                      <span style={{ fontSize: 11, color: "rgba(255,255,255,.85)" }}>Step 1 of 4</span>
                    </div>
                    <div style={{ height: 30, background: "repeating-linear-gradient(90deg, var(--jrni-color-neutral-6) 0 120px, transparent 120px 136px)" }} />
                  </div>
                </Section>
                <Section title="What customers can do here" description="Turning one off removes its section and drops it out of search results.">
                  <ToggleRow title="Appointments" description={`Book a time with a named ${staffNoun}.`} checked={simple.inc.appt} onChange={setInc("appt")} />
                  <ToggleRow title="Walk-Ins" description="Join a line now and see the current wait." checked={simple.inc.walkin} onChange={setInc("walkin")} />
                  <ToggleRow title="Events" description="Register for what's coming up." checked={simple.inc.events} onChange={setInc("events")} />
                </Section>
                <Section title="Sign in" description="Signed-in customers see their next appointment and their place in line, and skip the details form.">
                  <ToggleRow title="Offer sign-in" description="A sign-in panel beside the search card, and a Sign in button in the header." checked={simple.on.signin} onChange={setOn("signin")} />
                  {simple.on.signin ? (
                    <Field label="How customers sign in" hint={simple.signIn === "sso" ? "One button; the customer signs in with the organisation's own login and comes straight back." : "Email and password in the panel, with forgot-password. Existing customers only until self-registration is ruled on."}>
                      <SegmentedControl options={[{ value: "sso", label: "Tenant SSO" }, { value: "password", label: "Email & password" }]} value={simple.signIn} onChange={(v: any) => patch((s) => ({ ...s, signIn: v }))} size="sm" aria-label="Sign-in method" />
                    </Field>
                  ) : null}
                </Section>
              </>) : null}
              {section === "hero" ? (<>
                <Section title="Search for help" description="Plain-words search across everything the page offers. Results replace the page below the hero.">
                  <ToggleRow title="Search box" description="The card in the hero." checked={simple.on.search} onChange={setOn("search")} />
                  <ToggleRow title="Common questions" description="Pills under the search box for one-tap answers. Auto-generation from booking volume is requested; hand-authored today." checked={simple.on.pills} onChange={setOn("pills")} disabled={!simple.on.search} />
                </Section>
                {simple.layout === "l2" ? (
                  <Section title="Site-landing hero" description="The headline band, the jump shortcuts, and one event given the full treatment.">
                    <ToggleRow title="Hero shortcuts" description="One button per journey, each opening that journey's published page." checked={simple.on.heroLinks} onChange={setOn("heroLinks")} />
                    <ToggleRow title="Featured event" description="Auto picks the most-registered event; choose one to override." checked={simple.on.featured} onChange={setOn("featured")} disabled={!simple.inc.events} />
                    {simple.on.featured && simple.inc.events ? (
                      <Field label="Which event">
                        <Dropdown options={eventOptions} value={simple.featuredId ?? ""} onChange={(v: any) => patch((s) => ({ ...s, featuredId: v ? String(v) : null }))} aria-label="Featured event" />
                      </Field>
                    ) : null}
                  </Section>
                ) : null}
              </>) : null}
              {section === "sections" ? (
                <Section title="Below the hero" description="Top to bottom as they appear.">
                  {(simple.layout === "l2" ? ["events", "appts", "walkins"] : ["walkins", "appts", "events"]).map((k) => k === "walkins"
                    ? <ToggleRow key={k} title={simple.layout === "l2" ? "Walk in today" : "Walk in now"} description="Open desks and the wait right now." checked={simple.on.walkins} onChange={setOn("walkins")} disabled={!simple.inc.walkin} />
                    : k === "appts"
                    ? <ToggleRow key={k} title="Book a time" description="The next openings this week." checked={simple.on.appts} onChange={setOn("appts")} disabled={!simple.inc.appt} />
                    : <ToggleRow key={k} title="Coming up" description="Events open for registration." checked={simple.on.events} onChange={setOn("events")} disabled={!simple.inc.events} />)}
                  <ToggleRow title="All services" description={C.directoryTitle + " — the full directory, for customers who know what they want."} checked={simple.on.directory} onChange={setOn("directory")} />
                  <ToggleRow title="Still need help" description="Contact links at the foot of the page." checked={simple.on.help} onChange={setOn("help")} />
                </Section>
              ) : null}
              {section === "launch" ? (<>
                <Section title="Public link">
                  {liveUrl ? <CopyableUrl url={liveUrl} /> : null}
                </Section>
                <Section title="Embed on your own page">
                  <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "4px 0 16px" }}>
                    <Field label="Width">
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <SegmentedControl options={[{ value: "auto", label: "Auto" }, { value: "px", label: "px" }]} value={embed.unit} onChange={(v: any) => setEmbed((e) => ({ ...e, unit: v }))} size="sm" aria-label="Width unit" />
                        {embed.unit === "px" ? <Input type="number" value={embed.width} onChange={(e: any) => setEmbed((x) => ({ ...x, width: Number(e?.target?.value ?? e) || 960 }))} trailing="px" /> : null}
                      </div>
                    </Field>
                    <Field label="Height" hint={embed.resize === "auto" ? "The page tells the host its height, so nothing inside ever scrolls." : "A fixed frame; the page scrolls inside it."}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <SegmentedControl options={[{ value: "auto", label: "Grow with content" }, { value: "fixed", label: "Fixed" }]} value={embed.resize} onChange={(v: any) => setEmbed((e) => ({ ...e, resize: v }))} size="sm" aria-label="Height mode" />
                        {embed.resize === "fixed" ? <Input type="number" value={embed.height} onChange={(e: any) => setEmbed((x) => ({ ...x, height: Number(e?.target?.value ?? e) || 900 }))} trailing="px" /> : null}
                      </div>
                    </Field>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <SectionLabel tone="soft">Paste this where OneStop should appear</SectionLabel>
                      <div style={{ flex: "1 1 auto" }}></div>
                      <Button variant="secondary" size="sm" iconLeft={(UI as any).IconCopy ? React.createElement((UI as any).IconCopy, { size: 13 }) : undefined} onClick={copyEmbed} disabled={!embedCode}>{copiedEmbed ? "Copied" : "Copy code"}</Button>
                    </div>
                    <pre style={{ margin: 0, padding: 14, borderRadius: "var(--jrni-radius-sm)", background: "var(--jrni-color-neutral-1)", color: "var(--jrni-color-base-white)", fontFamily: "var(--jrni-font-family-mono)", fontSize: 12, lineHeight: 1.6, overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{embedCode}</pre>
                  </div>
                </Section>
              </>) : null}
            </div>
          </div>
        ) : null}

        {previewOnly || !onePane || !formOpen ? (
        <section style={{ flex: "1 1 620px", minWidth: previewOnly || onePane ? 0 : 440, display: "flex", flexDirection: "column", minHeight: 0, background: "var(--jrni-color-neutral-6)" }}>
          {!previewOnly ? (
            <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 12, rowGap: 8, flexWrap: "wrap", padding: "10px 16px", borderBottom: "1px solid var(--jrni-color-surface-border)", background: "var(--jrni-color-surface-card)" }}>
              {onePane ? null : (
                <Button variant="ghost" size="sm" iconOnly={(UI as any).IconList ? React.createElement((UI as any).IconList, { size: 15 }) : undefined} aria-label={formOpen ? "Hide the settings pane" : "Show the settings pane"} onClick={() => setFormOpen((v) => !v)} />
              )}
              <span style={{ fontSize: 13, fontWeight: 600 }}>{journeyItems.find((j) => j.id === step)?.label ?? "Home"}</span>
              <div style={{ flex: "1 1 auto" }}></div>
              {/* The simulation is announced, always. A booking reference on
                  screen that no system holds has to be labelled where the
                  operator is looking, not in a release note. */}
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--jrni-color-text-soft)" }}>
                <span style={{ width: 6, height: 6, borderRadius: 999, background: simulate ? "var(--jrni-color-semantic-green-1)" : "var(--jrni-color-surface-border)" }}></span>
                {simulate ? "Book / Join / Register open the journey in a new tab" : "Buttons disabled, as on a preview host"}
              </span>
              {simulate ? (
                <Button hierarchy="secondary" size="sm" onClick={() => setSimulate(false)} aria-pressed={true}>Buttons: live</Button>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setSimulate(true)} aria-pressed={false}>Buttons: off</Button>
              )}
              {simulate ? <Button variant="ghost" size="sm" onClick={() => { setSimEpoch((e) => e + 1); setLastHandoff(null); }}>Start over</Button> : null}
              <DevicePreviewToggle value={device} onChange={(v: any) => setDevice(v)} size="sm" />
            </div>
          ) : null}
          <div className="apd-scroll" style={{ flex: "1 1 auto", overflow: "auto", padding: previewOnly ? 0 : 12, display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
            <div className={device === "mobile" && !previewOnly ? "os-frame-mobile" : "os-frame-wide"} style={{ width: "100%", maxWidth: frameWidth, minHeight: "100%", display: "flex", flexDirection: "column", background: "var(--jrni-color-base-white)", border: previewOnly ? 0 : "1px solid var(--jrni-color-surface-border)", borderRadius: previewOnly ? 0 : "var(--jrni-radius-lg)", boxShadow: previewOnly ? "none" : "var(--jrni-shadow-lg)", overflow: "hidden", position: "relative" }}>
            {page.status === "loading" ? (
              <div style={{ padding: 48, textAlign: "center", fontSize: 13, color: "var(--jrni-color-text-soft)" }}>Loading the OneStop page…</div>
            ) : page.status === "error" ? (
              <div style={{ padding: 48, textAlign: "center", fontSize: 13, color: "var(--jrni-color-semantic-red-1)" }}>Couldn't load the OneStop page: {page.message}</div>
            ) : (
              <OneStopRoot>
                <OneStopTheme tint={tint} />
                <OneStopSessionProvider value={sessionState as any}>
                  <OneStopActionsProvider key={`sim-${simulate ? simEpoch : "off"}`} actions={previewActions}>
                  {chrome.header ? (
                    /* The same header the Appointments journey draws — brand bar, tenant name, page
                       title on the right. No sign-in button here (operator, 2026-09-04): the identity
                       panel owns sign-in. */
                    <div style={{ display: "flex", alignItems: "center", gap: 12, rowGap: 8, flexWrap: "wrap", padding: "14px 20px", borderBottom: "1px solid var(--jrni-color-surface-border)", background: tint.primary1 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: "#FFFFFF", letterSpacing: "-0.01em" }}>{tenantName}</span>
                      <div style={{ flex: "1 1 auto" }}></div>
                      <span style={{ fontSize: 12, color: "rgba(255, 255, 255, 0.85)" }}>OneStop</span>
                    </div>
                  ) : null}
                  {sso ? (
                    <div style={{ padding: "96px 28px", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, textAlign: "center" }}>
                      <span style={{ fontSize: 18, fontWeight: 700 }}>Taking you to {tenantName} sign-in…</span>
                      <span style={{ fontSize: 13, color: "var(--jrni-color-text-soft)" }}>You'll come straight back here once you're signed in.</span>
                    </div>
                  ) : (<>
                    {heroEnd > 0 ? (
                      signedIn || !simple.on.signin ? <BlockList blocks={heroBlocks} ctx={ctx} heroEyebrow={doc.header.subtitle} /> : (
                        /* Signed-out hero: the renderer draws the banner and the search card; the
                           sign-in panel is this host's, because the shared panel's button opens the
                           email-code sheet and the operator wants SSO or email + password here
                           (2026-09-04). Same classes, same components, same grid. */
                        <section className="lv-hero">
                          {doc.header.subtitle ? <span className="lv-hero-eyebrow">{doc.header.subtitle}</span> : null}
                          {heroBlocks.filter((b) => b.type === "context-banner").map((b) => renderBlock(b, ctx))}
                          <div className="lv-hero-grid">
                            <div>{heroBlocks.filter((b) => b.type === "search").map((b) => renderBlock(b, ctx))}</div>
                            <div className="lv-panel">
                              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                <span className="lv-panel-eyebrow">{C.signedOutEyebrow}</span>
                                <span className="lv-panel-title">{C.signedOutTitle}</span>
                              </div>
                              {simple.signIn === "sso" ? (
                                <Button hierarchy="primary" full onClick={startSso}>Sign in</Button>
                              ) : (
                                <form style={{ display: "flex", flexDirection: "column", gap: 9 }} onSubmit={(e) => { e.preventDefault(); if (!pwEmail.trim() || !pwPass) { setPwNote("Enter your email and password."); return; } setPwNote(null); setWho("customer"); }}>
                                  <Input ref={emailRef as any} type="email" full value={pwEmail} placeholder="Email" aria-label="Email" autoComplete="username" onChange={(e: any) => setPwEmail(e.target.value)} />
                                  <Input type="password" full value={pwPass} placeholder="Password" aria-label="Password" autoComplete="current-password" onChange={(e: any) => setPwPass(e.target.value)} />
                                  <Button hierarchy="primary" full type="submit">Sign in</Button>
                                  <button type="button" onClick={() => setPwNote("If that address has an account, a reset link is on its way.")} style={{ alignSelf: "flex-start", fontFamily: "inherit", fontSize: 13, fontWeight: 600, color: "var(--jrni-color-primary-text)", background: "transparent", border: 0, padding: 0, cursor: "pointer" }}>Forgot password?</button>
                                  {pwNote ? <span className="lv-soft" role="status">{pwNote}</span> : null}
                                </form>
                              )}
                            </div>
                          </div>
                        </section>
                      )
                    ) : null}
                    {q ? (
                      <section style={{ padding: "28px 28px 0" }}>
                        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 20, marginBottom: 10 }}>
                          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: "-0.01em" }}>{searchBlocks.length ? `Results for “${submitted.trim()}”` : `Nothing matched “${submitted.trim()}”`}</h2>
                          <button type="button" onClick={search.onClear} style={{ fontSize: 13, fontWeight: 600, color: "var(--jrni-color-primary-text)", background: "transparent", border: 0, padding: 0, cursor: "pointer", fontFamily: "inherit" }}>Clear search</button>
                        </div>
                        {searchBlocks.length ? null : <p style={{ margin: 0, fontSize: 14, color: "var(--jrni-color-text-soft)" }}>Try the words on your paperwork, or pick one of the suggestions above.</p>}
                      </section>
                    ) : null}
                    {q ? <BlockList blocks={searchBlocks} ctx={ctx} /> : groupShortcuts(middle).map((g) => g.length > 1 ? (
                      /* The three hero shortcuts on one line. Each is still the renderer's own
                         ButtonBlock; only the row is this host's. */
                      <div key={g[0]!.id} className="os-shortcuts" style={{ display: "flex", flexWrap: "wrap", gap: 10, padding: "20px 28px 0" }}>
                        {g.map((b) => <div key={b.id} style={{ flex: "0 0 auto" }}><BlockList blocks={[b]} ctx={ctx} /></div>)}
                      </div>
                    ) : g.map((b) => {
                      /* No dimming and
                         no "Hidden until that journey is published" badge — see the note on
                         `journeySlugs` above. */
                      return (
                        <div key={b.id}>
                          <BlockList blocks={[b]} ctx={ctx} />
                          {(rendered.find((r) => r.id === b.id)?.notice) ? <div style={{ margin: "0 28px 6px", fontSize: 11, color: "var(--jrni-color-text-soft)" }}>{rendered.find((r) => r.id === b.id)!.notice}</div> : null}
                        </div>
                      );
                    }))}
                    {trailing.length ? <BlockList blocks={trailing} ctx={ctx} /> : null}
                    {chrome.footer ? (
                      <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", padding: "14px 24px", borderTop: "1px solid var(--jrni-color-surface-border)", background: "var(--jrni-color-neutral-6)", fontSize: 12, color: "var(--jrni-color-text-soft)" }}>
                        <span>© 2026 {tenantName}</span>
                        <a href="#privacy">Privacy</a>
                        <a href="#accessibility">Accessibility</a>
                        <div style={{ flex: "1 1 auto" }}></div>
                        <span>{C.helpTitle}</span>
                      </div>
                    ) : null}
                  </>)}
                  </OneStopActionsProvider>
                </OneStopSessionProvider>
              </OneStopRoot>
            )}
            {/* WHAT THE LAST TAP HANDED OVER. The question a builder preview has
                to answer about a handoff is not "did a tab open" but "did the
                thing I tapped travel with it" — so the resolved URL is printed
                here, query string and all. */}
            {lastHandoff ? (
              <div style={{ margin: "8px auto 0", maxWidth: 720, display: "flex", alignItems: "baseline", gap: 8, fontSize: 11, color: "var(--jrni-color-text-soft)", flexWrap: "wrap" }}>
                <span style={{ fontWeight: 600 }}>Opened in a new tab:</span>
                <span>{lastHandoff.label}</span>
                <code style={{ fontFamily: "var(--jrni-font-family-mono, ui-monospace)", fontSize: 11, wordBreak: "break-all" }}>{lastHandoff.url}</code>
              </div>
            ) : null}
            </div>
          </div>
        </section>
        ) : null}
      </div>
    </div>
  );
}
